import type { ManagedVisibleWhen } from '@onefootprint/request-types/dashboard';

// Read/write a managed node's opaque config by the dotted `path` a Control declares (e.g.
// `key_people` or `screening.pep`). Writes are immutable so the reducer/diffing see a new object.

export const getPath = (obj: Record<string, unknown>, path: string): unknown =>
  path.split('.').reduce<unknown>((acc, key) => (acc as Record<string, unknown> | undefined)?.[key], obj);

export const setPath = (obj: Record<string, unknown>, path: string, value: unknown): Record<string, unknown> => {
  const [head, ...rest] = path.split('.');
  if (rest.length === 0) return { ...obj, [head]: value };
  const child = (obj[head] as Record<string, unknown> | undefined) ?? {};
  return { ...obj, [head]: setPath(child, rest.join('.'), value) };
};

// Evaluate a Control's structured `visibleWhen` against the current config. An absent condition — or an
// unknown future `kind` — stays visible so a control is never silently hidden.
export const isControlVisible = (
  visibleWhen: ManagedVisibleWhen | undefined,
  config: Record<string, unknown>,
): boolean => {
  if (!visibleWhen) return true;
  switch (visibleWhen.kind) {
    case 'truthy':
      return Boolean(getPath(config, visibleWhen.path));
    case 'equals':
      return String(getPath(config, visibleWhen.path) ?? '') === visibleWhen.value;
    case 'not_equals':
      return String(getPath(config, visibleWhen.path) ?? '') !== visibleWhen.value;
    default:
      return true;
  }
};
